import { useId } from 'react';
import { ArrowRight, Hourglass, ShieldCheck } from 'lucide-react';
import { useAuthOptionnel } from '../../auth/useAuth';
import { ErreurApi } from '../../lib/api';
import {
  MOTIF_MAX,
  MOTIF_MIN,
  useRetirerDemande,
  type ChangementDemande,
  type DemandeEnAttente,
} from '../../lib/useModificationsSensibles';
import { horodatageReunion } from '../../lib/emargement-affichage';
import { MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';

/**
 * Éléments communs d'une modification sensible (TF-437), là où la donnée se
 * corrige : ce que la demande change, son motif, la demande qui attend la
 * direction. Qui ouvre la file de la direction applique ses propres gestes ;
 * c'est l'API qui le dit (`dir-modifications`), pas un profil écrit ici.
 */

export function useValidationDirecte(): boolean {
  return useAuthOptionnel()?.peutAcceder('dir-modifications') ?? false;
}

export function motifValide(motif: string): boolean {
  const longueur = motif.trim().length;
  return longueur >= MOTIF_MIN && longueur <= MOTIF_MAX;
}

export function ListeChangements({ changements }: { changements: ChangementDemande[] }): JSX.Element {
  return (
    <ul className="space-y-1">
      {changements.map((changement) => (
        <li key={changement.champ} className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-xs">
          <span className="font-semibold text-codialis-navy">{changement.libelle} :</span>
          <span className="break-words text-codialis-gray line-through decoration-codialis-gray/50">
            {changement.avant}
          </span>
          <ArrowRight className="h-3 w-3 shrink-0 self-center text-codialis-gray" aria-label="devient" />
          <span className="break-words font-semibold text-codialis-navy">{changement.apres}</span>
        </li>
      ))}
    </ul>
  );
}

/** Le motif de la demande, lu par la direction et gardé au registre. */
export function ChampMotif({
  valeur,
  onChange,
  directe,
  placeholder,
}: {
  valeur: string;
  onChange: (valeur: string) => void;
  directe: boolean;
  placeholder?: string;
}): JSX.Element {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {directe ? 'Motif de la correction' : 'Motif de la demande'}
      </label>
      <textarea
        id={id}
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value)}
        rows={2}
        maxLength={MOTIF_MAX}
        required
        aria-describedby={`${id}-aide`}
        placeholder={placeholder ?? 'Ce qui justifie la correction (pièce reçue, erreur de saisie…)'}
        className="w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy"
      />
      <p id={`${id}-aide`} className="mt-1 text-[11px] text-codialis-gray">
        {directe
          ? 'Gardé au registre des modifications sensibles avec ce qui change.'
          : `Lu par la direction, qui approuve ou refuse. ${MOTIF_MIN} caractères au moins.`}
      </p>
    </div>
  );
}

/**
 * Ce que le geste va faire : une demande à la direction, ou une correction
 * appliquée aussitôt — par la direction, ou par le pôle pédagogique pour une
 * présence (TF-476).
 */
export function AvisValidation({ directe, parLePole = false }: { directe: boolean; parLePole?: boolean }): JSX.Element {
  return (
    <p className="flex items-start gap-2 rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-codialis-blue" aria-hidden />
      <span>
        {!directe
          ? 'Modification sensible : elle part à la direction pour validation. Rien ne change avant sa décision.'
          : parLePole
            ? 'Modification sensible : le pôle pédagogique l’applique directement. Elle reste inscrite au registre, que la direction consulte.'
            : 'Modification sensible : en tant que direction, vous l’appliquez directement. Elle reste inscrite au registre.'}
      </span>
    </p>
  );
}

/** Une demande qui attend la direction : ce qu'elle change, pourquoi, et son retrait par son auteur. */
export function DemandeEnCours({
  demande,
  onRetiree,
}: {
  demande: DemandeEnAttente;
  /** Après le retrait, pour l'écran qui tient la donnée hors du cache. */
  onRetiree?: () => void;
}): JSX.Element {
  const auteur = useAuthOptionnel()?.utilisateur?.id === demande.demandeParId;
  const retirer = useRetirerDemande();
  const toast = useToast();
  return (
    <div className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-amber-900 sm:p-4">
      <p className="flex items-start gap-2 text-sm font-semibold">
        <Hourglass className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {demande.typeLibelle} demandée le {horodatageReunion(demande.demandeLe)}
          {demande.demandePar ? ` par ${demande.demandePar}` : ''} : en attente de la direction.
        </span>
      </p>
      {demande.changements.length > 0 && <ListeChangements changements={demande.changements} />}
      <p className="whitespace-pre-line break-words text-xs">Motif : « {demande.motif} »</p>
      {retirer.error && (
        <MessageErreur
          message={retirer.error instanceof ErreurApi ? retirer.error.message : 'La demande n’a pas pu être retirée.'}
        />
      )}
      {auteur && (
        <button
          type="button"
          disabled={retirer.isPending}
          onClick={() =>
            retirer.mutate(demande.id, {
              onSuccess: (resultat) => {
                toast(resultat.message);
                onRetiree?.();
              },
            })
          }
          className="inline-flex min-h-[40px] items-center rounded-lg px-1 text-xs font-semibold text-amber-900 underline-offset-2 hover:underline disabled:opacity-50 [@media(pointer:coarse)]:min-h-[44px]"
        >
          {retirer.isPending ? 'Retrait…' : 'Retirer ma demande'}
        </button>
      )}
    </div>
  );
}
